/**
 * Client detail — Info, Products, Dependents, and Activity timeline.
 * SSN is masked by default with a "reveal" for the owning agent.
 * Products are simple (type + note); dependents carry the fields ACA
 * needs (name required, the rest optional).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Loader2, Eye, EyeOff, Plus, Trash2, Save, Phone, Mail, Clock,
  CalendarClock, CalendarPlus, MessageSquare, Check, RefreshCw, Camera, X, Printer, FileDown,
} from 'lucide-react';
import { clientsAPI, appointmentsAPI } from '@/lib/api';
import { getErrorMessage, formatPhone } from '@/lib/utils';
import ContactSmsCard from '@/components/sms/ContactSmsCard';
import ClientNeedsAnalysisCard from '@/components/clients/ClientNeedsAnalysisCard';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { DocumentsSection } from '@/components/common/DocumentsSection';

const PRODUCT_TYPES = ['Life', 'Ancillary', 'Medicare', 'Medicare Advantage', 'ACA', 'Dental', 'Vision', 'Hospital Indemnity', 'Accident', 'Annuity', 'Final Expense', 'Other'];
const TABS = [
  { key: 'info', label: 'Info' },
  { key: 'notes', label: 'Notes' },
  { key: 'documents', label: 'Documents' },
  { key: 'products', label: 'Products' },
  { key: 'dependents', label: 'Dependents' },
  { key: 'followups', label: 'Follow-ups' },
  { key: 'appointments', label: 'Appointments' },
  { key: 'messages', label: 'Messages' },
  { key: 'activity', label: 'Activity' },
];

function ClientPhoto({ client, onChanged }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef();
  const initials = `${(client.first_name || '')[0] || ''}${(client.last_name || '')[0] || ''}`.toUpperCase() || 'C';

  useEffect(() => {
    let revoked = null;
    if (client.photo_url) {
      clientsAPI.photoBlob(client.client_id)
        .then((r) => { const u = URL.createObjectURL(r.data); revoked = u; setUrl(u); })
        .catch(() => setUrl(null));
    } else { setUrl(null); }
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [client.client_id, client.photo_url]);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    clientsAPI.uploadPhoto(client.client_id, f)
      .then(() => { toast.success('Photo updated'); onChanged && onChanged(); })
      .catch((err) => toast.error(getErrorMessage(err, 'Upload failed')))
      .finally(() => { setBusy(false); if (fileRef.current) fileRef.current.value = ''; });
  };

  const remove = () => {
    setBusy(true);
    clientsAPI.deletePhoto(client.client_id)
      .then(() => { toast.success('Photo removed'); onChanged && onChanged(); })
      .catch((err) => toast.error(getErrorMessage(err, 'Remove failed')))
      .finally(() => setBusy(false));
  };

  return (
    <div className="relative group h-14 w-14 shrink-0" data-testid="client-photo">
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
              className="h-14 w-14 rounded-full overflow-hidden bg-teal-600/10 grid place-items-center relative"
              data-testid="client-photo-upload-btn" title="Upload photo">
        {url ? (
          <img src={url} alt={initials} className="h-full w-full object-cover" data-testid="client-photo-img" />
        ) : (
          <span className="text-teal-700 font-semibold text-lg" data-testid="client-photo-initials">{initials}</span>
        )}
        <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
          {busy ? <Loader2 className="h-4 w-4 text-white animate-spin" /> : <Camera className="h-4 w-4 text-white" />}
        </span>
      </button>
      {url && !busy && (
        <button type="button" onClick={remove} data-testid="client-photo-remove-btn"
                className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-rose-600 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
          <X size={11} />
        </button>
      )}
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid="client-photo-input" />
    </div>
  );
}


export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialTab = new URLSearchParams(window.location.search).get('tab');
  const [tab, setTab] = useState(initialTab || 'info');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await clientsAPI.get(id);
      setClient(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load client'));
      navigate('/clients');
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const deleteClient = async () => {
    if (!window.confirm('Delete this client? They will be removed from your active list.')) return;
    try {
      await clientsAPI.archive(id);
      toast.success('Client deleted');
      navigate('/clients');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not delete the client'));
    }
  };

  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async (print = false) => {
    setPdfBusy(true);
    try {
      const r = await clientsAPI.pdf(id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      if (print) {
        const w = window.open(url);
        if (w) w.addEventListener('load', () => { try { w.print(); } catch (_) { /* no-op */ } });
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(client?.first_name || 'client')}-${client?.last_name || ''}`.trim().replace(/\s+/g, '-') + '-profile.pdf';
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not generate the PDF'));
    } finally { setPdfBusy(false); }
  };

  if (loading || !client) {
    return <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="client-detail-page">
      <button onClick={() => navigate('/clients')} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1.5 mb-4" data-testid="client-back">
        <ArrowLeft size={15} /> Back to Clients
      </button>

      <div className="flex items-center gap-3 mb-5">
        <ClientPhoto client={client} onChanged={load} />
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{client.first_name} {client.last_name || ''}</h1>
          <div className="flex items-center gap-3 text-sm text-slate-500 mt-0.5">
            {client.phone && <span className="flex items-center gap-1"><Phone size={12} /> {formatPhone(client.phone)}</span>}
            {client.email && <span className="flex items-center gap-1"><Mail size={12} /> {client.email}</span>}
            {client.owner_agent_name && <Badge variant="outline" className="text-[10px]">Agent: {client.owner_agent_name}</Badge>}
          </div>
        </div>
        {client.phone && (
          <div className="flex gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => {
                if (window.krSoftphoneDial) window.krSoftphoneDial(client.phone, { contact_type: 'client', contact_id: client.client_id || client.id });
                else toast.error('Softphone unavailable');
              }}
              data-testid="client-call-btn"
            >
              <Phone size={14} className="mr-1.5" /> Call
            </Button>
            <Button variant="outline" size="sm" onClick={() => setTab('messages')} data-testid="client-text-btn">
              <MessageSquare size={14} className="mr-1.5" /> Text
            </Button>
          </div>
        )}
        <Button
          variant="outline" size="sm"
          onClick={() => downloadPdf(true)}
          disabled={pdfBusy}
          data-testid="client-print-btn"
        >
          {pdfBusy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Printer size={14} className="mr-1.5" />} Print
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={() => downloadPdf(false)}
          disabled={pdfBusy}
          data-testid="client-pdf-btn"
        >
          {pdfBusy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileDown size={14} className="mr-1.5" />} PDF
        </Button>
        <Button
          variant="outline" size="sm"
          className="text-rose-600 border-rose-200 hover:bg-rose-50"
          onClick={deleteClient}
          data-testid="client-delete-btn"
        >
          <Trash2 size={14} className="mr-1.5" /> Delete
        </Button>
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === t.key ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`client-tab-${t.key}`}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'info' && <InfoTab client={client} onSaved={load} />}
      {tab === 'notes' && <NotesTab clientId={id} />}
      {tab === 'documents' && (
        <Card><CardContent className="p-5" data-testid="client-documents-tab">
          <DocumentsSection entityId={id} docsApi={clientsAPI} title="Files, photos & documents" testPrefix="client-doc" />
        </CardContent></Card>
      )}
      {tab === 'products' && <ProductsTab client={client} onSaved={load} />}      {tab === 'dependents' && <DependentsTab client={client} onSaved={load} />}
      {tab === 'followups' && <FollowUpsTab clientId={id} />}
      {tab === 'appointments' && <AppointmentsTab client={client} />}
      {tab === 'messages' && (
        <Card><CardContent className="p-4" data-testid="client-messages-tab">
          <ContactSmsCard contactType="client" contactId={id} phone={client.phone} embedded />
        </CardContent></Card>
      )}
      {tab === 'activity' && <ActivityTab clientId={id} />}
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      {children}
    </div>
  );
}

function InfoTab({ client, onSaved }) {
  const [form, setForm] = useState({
    first_name: client.first_name || '', last_name: client.last_name || '',
    date_of_birth: client.date_of_birth || '', phone: client.phone || '',
    email: client.email || '', income: client.income || '',
    preferred_language: client.preferred_language || 'en',
    address: client.address || '', city: client.city || '',
    state: client.state || '', zip_code: client.zip_code || '',
    notes: client.notes || '',
    medicare_eligible: !!client.medicare_eligible,
    medicare_mbi: client.medicare_mbi || '',
    medicare_part_a_date: client.medicare_part_a_date || '',
    medicare_part_b_date: client.medicare_part_b_date || '',
    medicaid_eligible: !!client.medicaid_eligible,
    medicaid_level: client.medicaid_level || '',
    medicaid_number: client.medicaid_number || '',
    medicaid_access_username: client.medicaid_access_username || '',
  });
  const [ssnInput, setSsnInput] = useState('');
  const [mcaidPwInput, setMcaidPwInput] = useState('');
  const [revealed, setRevealed] = useState(null);
  const [revealedPw, setRevealedPw] = useState(null);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reveal = async () => {
    if (revealed || revealedPw) { setRevealed(null); setRevealedPw(null); return; }
    try {
      const r = await clientsAPI.revealSsn(client.client_id);
      setRevealed(r.data.ssn || 'Not on file');
      setRevealedPw(r.data.medicaid_access_password || null);
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to reveal')); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (ssnInput.trim()) payload.ssn = ssnInput.trim();
      if (mcaidPwInput.trim()) payload.medicaid_access_password = mcaidPwInput.trim();
      await clientsAPI.update(client.client_id, payload);
      toast.success('Saved');
      setSsnInput(''); setMcaidPwInput('');
      setRevealed(null); setRevealedPw(null);
      onSaved();
    } catch (e) { toast.error(getErrorMessage(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
    <Card><CardContent className="p-5 space-y-4" data-testid="client-info-tab">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Labeled label="First name"><Input value={form.first_name} onChange={(e) => upd('first_name', e.target.value)} data-testid="info-first-name" /></Labeled>
        <Labeled label="Last name"><Input value={form.last_name} onChange={(e) => upd('last_name', e.target.value)} /></Labeled>
        <Labeled label="Date of birth"><Input type="date" value={form.date_of_birth} onChange={(e) => upd('date_of_birth', e.target.value)} /></Labeled>
        <Labeled label="Language">
          <select value={form.preferred_language} onChange={(e) => upd('preferred_language', e.target.value)}
            className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm bg-white" data-testid="client-language">
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </Labeled>
        <Labeled label="Income"><Input value={form.income} onChange={(e) => upd('income', e.target.value)} placeholder="Optional" /></Labeled>
        <Labeled label="Phone"><Input value={form.phone} onChange={(e) => upd('phone', e.target.value)} /></Labeled>
        <Labeled label="Email"><Input value={form.email} onChange={(e) => upd('email', e.target.value)} /></Labeled>
      </div>

      <div>
        <label className="text-xs font-medium text-slate-600">SSN</label>
        <div className="flex items-center gap-2">
          <Input
            value={ssnInput}
            onChange={(e) => setSsnInput(e.target.value)}
            placeholder={client.ssn_masked || 'Not on file — enter to set'}
            data-testid="info-ssn-input"
          />
          <Button type="button" variant="outline" size="sm" onClick={reveal} data-testid="info-ssn-reveal">
            {revealed ? <EyeOff size={14} /> : <Eye size={14} />}
          </Button>
        </div>
        {revealed && <p className="text-xs text-slate-600 mt-1 font-mono" data-testid="info-ssn-revealed">Full SSN: {revealed}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Labeled label="Address">
          <AddressAutocomplete
            value={form.address}
            onChange={(v) => upd('address', v)}
            onPlaceSelected={(parsed) => setForm((p) => ({
              ...p,
              address: parsed.street || parsed.formatted || p.address,
              city: parsed.city || p.city,
              state: parsed.state || p.state,
              zip_code: parsed.zip || p.zip_code,
            }))}
            testId="client-address-autocomplete"
          />
        </Labeled>
        <Labeled label="City"><Input value={form.city} onChange={(e) => upd('city', e.target.value)} /></Labeled>
        <Labeled label="State"><Input value={form.state} onChange={(e) => upd('state', e.target.value)} /></Labeled>
        <Labeled label="ZIP"><Input value={form.zip_code} onChange={(e) => upd('zip_code', e.target.value)} /></Labeled>
      </div>
      <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="eligibility-section">
        <p className="text-sm font-semibold text-slate-700">Eligibility</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="accent-teal-600" checked={form.medicare_eligible}
            onChange={(e) => upd('medicare_eligible', e.target.checked)} data-testid="elig-medicare" />
          Medicare
        </label>
        {form.medicare_eligible && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pl-6" data-testid="medicare-fields">
            <Labeled label="MBI"><Input value={form.medicare_mbi} onChange={(e) => upd('medicare_mbi', e.target.value)} placeholder="1EG4-TE5-MK73" data-testid="medicare-mbi" /></Labeled>
            <Labeled label="Part A date"><Input type="date" value={form.medicare_part_a_date} onChange={(e) => upd('medicare_part_a_date', e.target.value)} data-testid="medicare-part-a" /></Labeled>
            <Labeled label="Part B date"><Input type="date" value={form.medicare_part_b_date} onChange={(e) => upd('medicare_part_b_date', e.target.value)} data-testid="medicare-part-b" /></Labeled>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" className="accent-teal-600" checked={form.medicaid_eligible}
            onChange={(e) => upd('medicaid_eligible', e.target.checked)} data-testid="elig-medicaid" />
          Medicaid
        </label>
        {form.medicaid_eligible && (
          <div className="space-y-3 pl-6" data-testid="medicaid-fields">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Labeled label="Medicaid level">
                <select value={form.medicaid_level} onChange={(e) => upd('medicaid_level', e.target.value)}
                  className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="medicaid-level">
                  <option value="">— select —</option>
                  {['QMB+', 'QMB-', 'SLMB+', 'SLMB-', 'QI', 'FBDE'].map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </Labeled>
              <Labeled label="Medicaid number"><Input value={form.medicaid_number} onChange={(e) => upd('medicaid_number', e.target.value)} data-testid="medicaid-number" /></Labeled>
            </div>
            <p className="text-xs text-slate-500">Benefits portal / agency (Access · foodstamps · Medicaid) — optional, stored securely.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Labeled label="Portal username"><Input value={form.medicaid_access_username} onChange={(e) => upd('medicaid_access_username', e.target.value)} data-testid="medicaid-username" /></Labeled>
              <Labeled label="Portal password">
                <Input type="text" value={mcaidPwInput} onChange={(e) => setMcaidPwInput(e.target.value)}
                  placeholder={client.medicaid_access_password_set ? '•••••••• (set)' : 'Not set'} data-testid="medicaid-password" />
                {revealedPw && <p className="text-xs text-slate-600 mt-1 font-mono" data-testid="medicaid-password-revealed">Saved password: {revealedPw}</p>}
              </Labeled>
            </div>
          </div>
        )}
      </div>

      <Labeled label="Notes"><textarea rows={3} value={form.notes} onChange={(e) => upd('notes', e.target.value)} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" /></Labeled>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="info-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>
    </CardContent></Card>
    {client.needs_analysis && (
      <ClientNeedsAnalysisCard clientId={client.client_id} needsAnalysis={client.needs_analysis} />
    )}
    </div>
  );
}

function ProductsTab({ client, onSaved }) {
  const [products, setProducts] = useState(client.products || []);
  const [saving, setSaving] = useState(false);
  const add = () => setProducts((p) => [...p, { product_type: 'Life', note: '', status: 'active' }]);
  const remove = (i) => setProducts((p) => p.filter((_, idx) => idx !== i));
  const set = (i, k, v) => setProducts((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    setSaving(true);
    try {
      await clientsAPI.update(client.client_id, { products });
      toast.success('Products saved');
      onSaved();
    } catch (e) { toast.error(getErrorMessage(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Card><CardContent className="p-5 space-y-3" data-testid="client-products-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Active products / current plan</p>
        <Button size="sm" variant="outline" onClick={add} data-testid="add-product-btn"><Plus size={14} className="mr-1" /> Add product</Button>
      </div>
      {products.length === 0 ? (
        <p className="text-sm italic text-slate-400 py-6 text-center">No products yet.</p>
      ) : products.map((p, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-2.5 space-y-2" data-testid={`product-row-${i}`}>
          <div className="flex items-center gap-2">
            <select value={p.product_type} onChange={(e) => set(i, 'product_type', e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
              {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <Input value={p.note || ''} onChange={(e) => set(i, 'note', e.target.value)} placeholder="Note (optional)" className="h-9 text-sm flex-1" />
            <select value={p.status || 'active'} onChange={(e) => set(i, 'status', e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button onClick={() => remove(i)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`product-remove-${i}`}><Trash2 size={15} /></button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Input type="number" value={p.coverage_year || ''} onChange={(e) => set(i, 'coverage_year', e.target.value ? Number(e.target.value) : null)} placeholder="Coverage year" className="h-9 text-sm" data-testid={`product-coverage-year-${i}`} />
            <Input value={p.carrier || ''} onChange={(e) => set(i, 'carrier', e.target.value)} placeholder="Carrier" className="h-9 text-sm" data-testid={`product-carrier-${i}`} />
            <Input value={p.plan_name || ''} onChange={(e) => set(i, 'plan_name', e.target.value)} placeholder="Plan" className="h-9 text-sm" data-testid={`product-plan-${i}`} />
          </div>
        </div>
      ))}
      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} data-testid="products-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>
    </CardContent></Card>
  );
}

function DependentsTab({ client, onSaved }) {
  const [deps, setDeps] = useState((client.dependents || []).map((d) => ({ ...d, ssn: '' })));
  const [saving, setSaving] = useState(false);
  const add = () => setDeps((p) => [...p, { first_name: '', relationship: 'child', date_of_birth: '', phone: '', email: '', income: '', ssn: '' }]);
  const remove = (i) => setDeps((p) => p.filter((_, idx) => idx !== i));
  const set = (i, k, v) => setDeps((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    if (deps.some((d) => !d.first_name.trim())) { toast.error('Each dependent needs a first name'); return; }
    setSaving(true);
    try {
      // Only send ssn when the agent typed a new one; keep existing otherwise.
      const payload = deps.map((d) => {
        const out = { ...d };
        if (!out.ssn) delete out.ssn;
        delete out.ssn_masked;
        return out;
      });
      await clientsAPI.update(client.client_id, { dependents: payload });
      toast.success('Dependents saved');
      onSaved();
    } catch (e) { toast.error(getErrorMessage(e, 'Save failed')); }
    finally { setSaving(false); }
  };

  return (
    <Card><CardContent className="p-5 space-y-3" data-testid="client-dependents-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">Dependents (required for ACA)</p>
        <Button size="sm" variant="outline" onClick={add} data-testid="add-dependent-btn"><Plus size={14} className="mr-1" /> Add dependent</Button>
      </div>
      {deps.length === 0 ? (
        <p className="text-sm italic text-slate-400 py-6 text-center">No dependents yet.</p>
      ) : deps.map((d, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`dependent-row-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Dependent {i + 1}</span>
            <button onClick={() => remove(i)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`dependent-remove-${i}`}><Trash2 size={14} /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={d.first_name} onChange={(e) => set(i, 'first_name', e.target.value)} placeholder="First name *" className="h-9 text-sm" data-testid={`dep-first-name-${i}`} />
            <select value={d.relationship || 'child'} onChange={(e) => set(i, 'relationship', e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
              <option value="spouse">Spouse</option>
              <option value="child">Child</option>
              <option value="other">Other</option>
            </select>
            <Input type="date" value={d.date_of_birth || ''} onChange={(e) => set(i, 'date_of_birth', e.target.value)} className="h-9 text-sm" />
            <Input value={d.income || ''} onChange={(e) => set(i, 'income', e.target.value)} placeholder="Income (opt)" className="h-9 text-sm" />
            <Input value={d.phone || ''} onChange={(e) => set(i, 'phone', e.target.value)} placeholder="Phone (opt)" className="h-9 text-sm" />
            <Input value={d.email || ''} onChange={(e) => set(i, 'email', e.target.value)} placeholder="Email (opt)" className="h-9 text-sm" />
            <Input value={d.ssn || ''} onChange={(e) => set(i, 'ssn', e.target.value)} placeholder={d.ssn_masked || 'SSN (opt)'} className="h-9 text-sm col-span-2" />
          </div>
        </div>
      ))}
      <div className="flex justify-end pt-2">
        <Button onClick={save} disabled={saving} data-testid="dependents-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Save
        </Button>
      </div>
    </CardContent></Card>
  );
}

function FollowUpsTab({ clientId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await clientsAPI.listFollowUps(clientId);
      setItems(r.data?.follow_ups || []);
    } catch (_) { /* no-op */ } finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!when) { toast.error('Pick a date & time'); return; }
    setSaving(true);
    try {
      await clientsAPI.createFollowUp(clientId, new Date(when).toISOString(), note || null);
      toast.success('Follow-up scheduled (syncs to Google Calendar if connected)');
      setWhen(''); setNote('');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to schedule')); }
    finally { setSaving(false); }
  };
  const complete = async (fid) => { await clientsAPI.completeFollowUp(fid); await load(); };
  const remove = async (fid) => { if (window.confirm('Cancel this follow-up?')) { await clientsAPI.deleteFollowUp(fid); await load(); } };

  const syncLabel = (s) => ({ synced: 'Google ✓', user_not_connected: 'Google not connected', no_linked_user: 'No linked user', api_error: 'Sync error', error: 'Sync error' }[s] || null);

  return (
    <Card><CardContent className="p-5 space-y-4" data-testid="client-followups-tab">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-xs font-medium text-slate-600">Date & time</label>
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-9" data-testid="followup-when" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="text-xs font-medium text-slate-600">Note (optional)</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Review ACA renewal" className="h-9" data-testid="followup-note" />
        </div>
        <Button onClick={create} disabled={saving} data-testid="followup-add">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarPlus size={14} className="mr-1.5" /> Schedule</>}
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm italic text-slate-400 py-6 text-center">No follow-ups yet.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((f) => (
            <li key={f.follow_up_id} className={`flex items-center gap-3 border rounded-lg p-3 ${f.done ? 'bg-slate-50 opacity-70' : 'border-slate-200'}`} data-testid={`followup-${f.follow_up_id}`}>
              <CalendarClock size={16} className="text-teal-600" />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">{new Date(f.scheduled_at).toLocaleString()}</div>
                {f.note && <div className="text-xs text-slate-500">{f.note}</div>}
                {syncLabel(f.google_sync_status) && <span className="text-[10px] text-slate-400">{syncLabel(f.google_sync_status)}</span>}
              </div>
              {!f.done && <button onClick={() => complete(f.follow_up_id)} className="text-emerald-600 hover:text-emerald-700 p-1" title="Mark done" data-testid={`followup-done-${f.follow_up_id}`}><Check size={16} /></button>}
              <button onClick={() => remove(f.follow_up_id)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`followup-del-${f.follow_up_id}`}><Trash2 size={15} /></button>
            </li>
          ))}
        </ul>
      )}
    </CardContent></Card>
  );
}

function AppointmentsTab({ client }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [when, setWhen] = useState('');
  const [duration, setDuration] = useState('30');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await appointmentsAPI.list({ client_id: client.client_id });
      setItems(r.data?.appointments || []);
    } catch (_) { /* no-op */ } finally { setLoading(false); }
  }, [client.client_id]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!when) { toast.error('Pick a date & time'); return; }
    setSaving(true);
    try {
      await appointmentsAPI.createForClient({
        client_id: client.client_id,
        scheduled_at: new Date(when).toISOString(),
        duration_minutes: parseInt(duration, 10),
        notes: notes || undefined,
      });
      toast.success('Appointment booked (syncs to Google Calendar if connected)');
      setWhen(''); setNotes('');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to book appointment')); }
    finally { setSaving(false); }
  };
  const cancel = async (id) => { if (window.confirm('Cancel this appointment?')) { await appointmentsAPI.cancel(id); await load(); } };

  return (
    <Card><CardContent className="p-5 space-y-4" data-testid="client-appointments-tab">
      <div className="flex flex-wrap items-end gap-2">
        <div>
          <label className="text-xs font-medium text-slate-600">Date & time</label>
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-9" data-testid="appt-when" />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Length</label>
          <select value={duration} onChange={(e) => setDuration(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm block">
            {['15', '30', '45', '60'].map((d) => <option key={d} value={d}>{d} min</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="text-xs font-medium text-slate-600">Notes (optional)</label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-9" data-testid="appt-notes" />
        </div>
        <Button onClick={create} disabled={saving} data-testid="appt-add">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CalendarPlus size={14} className="mr-1.5" /> Book</>}
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm italic text-slate-400 py-6 text-center">No appointments yet.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((a) => (
            <li key={a.appointment_id} className={`flex items-center gap-3 border rounded-lg p-3 ${a.status === 'cancelled' ? 'bg-slate-50 opacity-60' : 'border-slate-200'}`} data-testid={`appt-${a.appointment_id}`}>
              <CalendarClock size={16} className="text-teal-600" />
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">{new Date(a.scheduled_at).toLocaleString()} · {a.duration_minutes} min</div>
                <div className="text-xs text-slate-500 capitalize">{a.status}</div>
              </div>
              {a.status !== 'cancelled' && <button onClick={() => cancel(a.appointment_id)} className="text-rose-500 hover:text-rose-700 p-1" data-testid={`appt-cancel-${a.appointment_id}`}><Trash2 size={15} /></button>}
            </li>
          ))}
        </ul>
      )}
    </CardContent></Card>
  );
}

function ActivityTab({ clientId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await clientsAPI.activity(clientId);
      setRows(r.data?.activity || []);
    } catch (_) { /* no-op */ } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await clientsAPI.addNote(clientId, note.trim());
      setNote('');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to add note')); }
    finally { setSaving(false); }
  };

  return (
    <Card><CardContent className="p-5 space-y-4" data-testid="client-activity-tab">
      <div className="flex items-center gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }} placeholder="Add a note to the timeline…" data-testid="activity-note-input" />
        <Button onClick={addNote} disabled={saving || !note.trim()} data-testid="activity-note-add">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm italic text-slate-400 py-6 text-center">No activity yet.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((a) => (
            <li key={a.activity_id} className="flex gap-3" data-testid={`activity-${a.activity_id}`}>
              <div className="mt-0.5"><Clock size={14} className="text-slate-400" /></div>
              <div className="flex-1">
                <div className="text-sm text-slate-800">{a.note || a.event_type.replace(/[_:]/g, ' ')}</div>
                <div className="text-[11px] text-slate-400">
                  {a.actor_name || 'System'} · {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                  {a.event_type ? ` · ${a.event_type}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </CardContent></Card>
  );
}

// Individual client notes — each note is its own timestamped entry
// (author + date), NOT one shared free-text box. Backed by the same
// activity timeline, filtered to human-written notes.
function NotesTab({ clientId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await clientsAPI.activity(clientId);
      const all = r.data?.activity || [];
      setRows(all.filter((a) => a.note && (a.event_type === 'note' || a.event_type === 'lead:note')));
    } catch (_) { /* no-op */ } finally { setLoading(false); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await clientsAPI.addNote(clientId, note.trim());
      setNote('');
      await load();
    } catch (e) { toast.error(getErrorMessage(e, 'Failed to add note')); }
    finally { setSaving(false); }
  };

  return (
    <Card><CardContent className="p-5 space-y-4" data-testid="client-notes-tab">
      <div className="flex items-start gap-2">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Write a note… (each note is saved separately with the date and author)"
          className="flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm resize-y"
          data-testid="client-note-input"
        />
        <Button onClick={addNote} disabled={saving || !note.trim()} data-testid="client-note-add">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add</>}
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
      ) : rows.length === 0 ? (
        <p className="text-sm italic text-slate-400 py-6 text-center" data-testid="client-notes-empty">No notes yet. Add your first note above.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((a) => (
            <li
              key={a.activity_id}
              className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5"
              data-testid={`client-note-${a.activity_id}`}
            >
              <div className="text-sm text-slate-800 whitespace-pre-wrap">{a.note}</div>
              <div className="text-[11px] text-slate-400 mt-1.5">
                {a.actor_name || 'System'} · {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                {a.event_type === 'lead:note' ? ' · from lead' : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </CardContent></Card>
  );
}
